import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import MenuBar from '../../components/menuBar/menuBar';
import PageFooter from '../../components/pageFooter/pageFooter';
import styles from '../../publicAsset/termsOfServicePage.module.scss';
import ContactConstant from '../../utils/Constant/ContactConstant';

const SECTIONS = [
    { id: 'who-can-use', label: '1. Who Can Use "prepal"?' },
    { id: 'content-platform', label: '2. Your Content and Our Platform' },
    { id: 'transparency', label: '3. Transparency on Technology Usage' },
    { id: 'early-access', label: '4. Early Access and Beta Use' },
    { id: 'payment', label: '5. Payment and Pricing' },
    { id: 'tutor-marketplace', label: '6. Tutor Marketplace (Future Build)' },
    { id: 'rules', label: '7. What You Cannot Do (The Rules)' },
    { id: 'disclaimers', label: '8. Disclaimers and Liability' },
    { id: 'governing-law', label: '9. Governing Law and Disputes' },
    { id: 'contact', label: '10. Contact Us' },
];

const TermsOfServicePage = () => {
    const [activeSection, setActiveSection] = useState('acceptance');
    const sectionRefs = useRef({});

    // Scroll-driven fixed sidebar — position:sticky breaks when html/body has overflow-x:hidden
    const heroRef = useRef(null);
    const sidebarWrapperRef = useRef(null);
    const sidebarRef = useRef(null);
    const containerRef = useRef(null);
    const [isFixed, setIsFixed] = useState(false);
    const [fixedStyle, setFixedStyle] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const update = () => {
            if (!heroRef.current || !sidebarWrapperRef.current || !containerRef.current || !sidebarRef.current) return;
            const heroBottom = heroRef.current.getBoundingClientRect().bottom;
            const wrapperRect = sidebarWrapperRef.current.getBoundingClientRect();
            const containerBottom = containerRef.current.getBoundingClientRect().bottom;
            // Use the aside's actual rendered height (wrapperRect.height collapses to 0 when aside is fixed)
            const sidebarHeight = sidebarRef.current.offsetHeight;
            const pastHero = heroBottom <= 88;
            const beforeFooter = containerBottom > sidebarHeight + 88 + 12; // 20px buffer before footer
            if (pastHero && beforeFooter) {
                setIsFixed(true);
                setFixedStyle({
                    position: 'fixed',
                    top: 88,
                    left: wrapperRect.left,
                    width: wrapperRect.width,
                });
            } else {
                setIsFixed(false);
                setFixedStyle({});
            }
        };
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
        );

        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            const offset = 90;
            const top = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <div className={styles.page}>
            <Helmet>
                <title>Terms of Service | Prepal</title>
                <meta name="description" content="Read Prepal's Terms of Service to understand your rights and responsibilities when using our AI-powered test preparation platform." />
                <meta name="keywords" content="Prepal terms of service, terms and conditions, user agreement, legal" />
                <meta name="author" content="Prepal Team" />
            </Helmet>

            <MenuBar />

            {/* Hero Banner */}
            <div className={styles.hero} ref={heroRef}>
                <div className={styles.heroContent}>
                    <p className={styles.heroEyebrow}>Legal</p>
                    <h1 className={styles.heroTitle}>Terms of Service</h1>
                    <p className={styles.heroSubtitle}>
                        Please read these terms carefully before using Prepal.
                        By accessing our platform, you agree to be bound by these terms.
                    </p>
                    <p className={styles.heroMeta}>Last Updated: <strong>March 5, 2026</strong></p>
                </div>
            </div>

            {/* Main Layout */}
            <div className={styles.container} ref={containerRef}>
                {/* Wrapper always holds flex space; inner aside goes fixed via JS */}
                <div ref={sidebarWrapperRef} className={styles.sidebarWrapper}>
                    <aside
                        ref={sidebarRef}
                        className={`${styles.sidebar} ${isFixed ? styles.sidebarFixed : ''}`}
                        style={fixedStyle}
                    >
                        <div className={styles.tocCard}>
                            <p className={styles.tocHeading}>Table of Contents</p>
                            <nav>
                                <ul className={styles.tocList}>
                                    {SECTIONS.map(({ id, label }) => (
                                        <li key={id}>
                                            <button
                                                className={`${styles.tocItem} ${activeSection === id ? styles.tocItemActive : ''}`}
                                                onClick={() => scrollToSection(id)}
                                            >
                                                {label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>
                    </aside>
                </div>

                {/* Content */}
                <main className={styles.content}>

                    <section className={styles.section}>
                        <p className={styles.text}>
                            Welcome to prepal! We're thrilled to have you here.
                        </p>
                        <p className={styles.text}>
                            This is the legal agreement between you and prepal (the "Company," "we," "us") regarding your use of the prepal adaptive learning platform (the "Service"). prepal is currently operating as a pre-incorporation entity and will be formally incorporated as a legal entity in 2026.
                        </p>
                        <p className={styles.text}>
                            We've tried to write these terms to be as clear as possible. When you create an account, log in, or use any part of our service, you agree to these Terms. If you don't agree with them, please don't use the Service.
                        </p>
                    </section>

                    <section id="who-can-use" className={styles.section} ref={el => sectionRefs.current['who-can-use'] = el}>
                        <h2 className={styles.sectionTitle}>1. Who Can Use &#8220;prepal&#8221;?</h2>
                        <p className={styles.text}>
                            We want everyone to be able to learn, but we have a few rules to keep things running smoothly.
                        </p>
                        <h3 className={styles.subSectionTitle}>A. Age Requirements</h3>
                        <p className={styles.text}>
                            If you are under 15 years old (the age of consent in Vietnam), you must have permission from a parent or legal guardian to use the Service. By creating an account, you confirm that you meet these requirements.
                        </p>
                        <h3 className={styles.subSectionTitle}>B. Registration &amp; Your Account</h3>
                        <p className={styles.text}>
                            You must provide accurate and complete information when you sign up. You are solely responsible for all activity that occurs under your password and account. Please keep your password safe!
                        </p>
                        <p className={styles.text}>
                            If you are a Parent setting up an account for your child, you are responsible for ensuring your child complies with these Terms.
                        </p>
                    </section>

                    <section id="content-platform" className={styles.section} ref={el => sectionRefs.current['content-platform'] = el}>
                        <h2 className={styles.sectionTitle}>2. Your Content and Our Platform</h2>
                        <p className={styles.text}>
                            prepal is built on two things: the quality of our content and the data generated from your learning.
                        </p>
                        <h3 className={styles.subSectionTitle}>A. Content We Provide (Our IP)</h3>
                        <p className={styles.text}>
                            The Service, including all diagnostic tests, practice questions, analytics algorithms, score predictions, design elements, text, graphics, and underlying code, is owned by the Company. It is protected by Intellectual Property laws in Vietnam and internationally.
                        </p>
                        <div className={styles.infoBox}>
                            <em>Simply Put: You are granted a license to use our content for your personal SAT preparation only. You cannot copy, distribute, reproduce, or resell our questions or explanations. If you do, we will have to suspend your account and take legal action.</em>
                        </div>
                        <h3 className={styles.subSectionTitle}>B. Your Learning Data (User Content)</h3>
                        <p className={styles.text}>
                            When you use prepal, you generate data: your answers, mistakes, time taken per question, and feedback notes ("User Content").
                        </p>
                        <p className={styles.text}>
                            <strong>What we use it for:</strong> You grant prepal a non-exclusive, royalty-free, perpetual license to use your aggregated, anonymized learning data to improve our adaptive algorithms, refine our question banks, and develop new features.
                        </p>
                        <div className={styles.infoBox}>
                            <em>Simply Put: Your data helps the platform get smarter for everyone. We will always protect your personal identity. See our Privacy Policy for full details.</em>
                        </div>
                    </section>

                    <section id="transparency" className={styles.section} ref={el => sectionRefs.current['transparency'] = el}>
                        <h2 className={styles.sectionTitle}>3. Transparency on Technology Usage</h2>
                        <h3 className={styles.subSectionTitle}>A. Third-Party Technology</h3>
                        <p className={styles.text}>
                            prepal utilizes APIs from third-party providers, including OpenAI, to generate dynamic explanations in our chatbot and during error analysis.
                        </p>
                        <p className={styles.text}>
                            <strong>Important:</strong> We commit to configuring these APIs to NOT use your conversations, prompts, or inputs to train their underlying models. Your direct conversations are used only for providing you with a real-time, personalized response.
                        </p>
                        <h3 className={styles.subSectionTitle}>B. Use of Anonymized Data</h3>
                        <p className={styles.text}>
                            We use your interaction data (conversation topics, test results, performance metrics) to update and improve prepal's proprietary adaptive algorithms and to refine your dashboard statistics. This data is processed internally and is anonymized when used for training our internal systems.
                        </p>
                        <h3 className={styles.subSectionTitle}>C. Accuracy and Limitations</h3>
                        <p className={styles.text}>
                            While our technology strives for the highest level of accuracy, no system is perfect. Generated content (explanations, score predictions) may occasionally be incomplete or contain factual errors.
                        </p>
                        <div className={styles.infoBox}>
                            <em>Simply Put: The platform is an excellent study tool, but it should not be the sole source of truth. If you encounter an error, please report it to us immediately.</em>
                        </div>
                    </section>

                    <section id="early-access" className={styles.section} ref={el => sectionRefs.current['early-access'] = el}>
                        <h2 className={styles.sectionTitle}>4. Early Access and Beta Use</h2>
                        <p className={styles.text}>
                            Since prepal is currently in its Early Access phase, please understand that the Service is a work in progress.
                        </p>
                        <h3 className={styles.subSectionTitle}>A. Service As-Is</h3>
                        <p className={styles.text}>
                            You agree to use the Service on an "as-is" basis, meaning it may contain bugs, errors, or inaccuracies. Data may occasionally be reset or lost as we deploy updates.
                        </p>
                        <h3 className={styles.subSectionTitle}>B. Feedback</h3>
                        <p className={styles.text}>
                            Your feedback is invaluable. By using the Service, you agree that we can use any feedback, suggestions, or ideas you submit without any obligation to compensate you.
                        </p>
                        <h3 className={styles.subSectionTitle}>C. Current Early Access Features</h3>
                        <p className={styles.text}>
                            During the Early Access phase, the following features are available:
                        </p>
                        <ul className={styles.list}>
                            <li>1,200+ practice questions across all 8 SAT topics (Reading &amp; Writing and Math)</li>
                            <li>Adaptive testing environment based on Item Response Theory (IRT)</li>
                            <li>Instant explanation chatbot for question help</li>
                            <li>Basic score tracking, visualization, and progress dashboards</li>
                            <li>Progressive hints system</li>
                            <li>User authentication and profile management</li>
                        </ul>
                        <p className={styles.text}>
                            Additional features including full-length diagnostic tests, personalized study plans, and a tutor marketplace are planned for future builds and will be made available to Early Access users at no additional cost.
                        </p>
                    </section>

                    <section id="payment" className={styles.section} ref={el => sectionRefs.current['payment'] = el}>
                        <h2 className={styles.sectionTitle}>5. Payment and Pricing</h2>
                        <h3 className={styles.subSectionTitle}>A. Early Access Pricing</h3>
                        <ol className={styles.orderedList}>
                            <li><strong>One-Time Payment:</strong> Early Access is offered as a one-time payment of $40 USD (approximately 1,050,000 VND). This is not a recurring subscription. Your payment grants full access to all current and future Early Access features.</li>
                            <li><strong>Payment Processing:</strong> We use Paddle as our secure third-party payment processor (Merchant of Record). Paddle accepts Visa, Mastercard, and other major payment methods. By making a purchase, you agree to Paddle's terms and acknowledge that your payment information is processed securely by them, not stored on our servers.</li>
                            <li><strong>VAT and Taxes:</strong> All prices displayed will clearly state whether they include or exclude Value Added Tax (VAT), as required by Vietnamese law. Paddle handles applicable tax collection as Merchant of Record.</li>
                        </ol>
                        <h3 className={styles.subSectionTitle}>B. Refunds and Cancellations</h3>
                        <p className={styles.text}>
                            Our Refund Policy is a separate document that forms part of these Terms. By purchasing the Service, you agree to the terms outlined in our Refund and Cancellation Policy.
                        </p>
                        <h3 className={styles.subSectionTitle}>C. Future Subscription Transition</h3>
                        <p className={styles.text}>
                            When prepal transitions from Early Access to a subscription model, Early Access purchasers will receive a permanent 50% discount on subscription pricing. Details of this transition will be communicated at least 30 days in advance.
                        </p>
                    </section>

                    <section id="tutor-marketplace" className={styles.section} ref={el => sectionRefs.current['tutor-marketplace'] = el}>
                        <h2 className={styles.sectionTitle}>6. Tutor Marketplace (Future Build)</h2>
                        <p className={styles.text}>
                            This section will be expanded when the Tutor Marketplace feature launches.
                        </p>
                        <h3 className={styles.subSectionTitle}>A. Tutor Services</h3>
                        <p className={styles.text}>
                            Once launched, prepal will facilitate one-on-one tutoring services provided by certified educators associated with our vetted tutor network.
                        </p>
                        <h3 className={styles.subSectionTitle}>B. Accountability and Quality</h3>
                        <p className={styles.text}>
                            Since prepal manages and vets all tutors on the platform, we take complaints about tutor quality seriously. Please report any issues immediately through our official complaint channel as detailed in the Complaint Handling Policy.
                        </p>
                    </section>

                    <section id="rules" className={styles.section} ref={el => sectionRefs.current['rules'] = el}>
                        <h2 className={styles.sectionTitle}>7. What You Cannot Do (The Rules)</h2>
                        <p className={styles.text}>
                            To keep prepal a positive and fair learning environment for everyone, you agree not to:
                        </p>
                        <ol className={`${styles.orderedList} ${styles.orderedListStart4}`} start={4}>
                            <li><strong>Cheat:</strong> Attempt to reverse engineer the adaptive algorithm, hack the testing engine, or share test questions and answers publicly.</li>
                            <li><strong>Harm the Service:</strong> Introduce viruses, malware, or other malicious code.</li>
                            <li><strong>Harass:</strong> Engage in any form of harassment, hate speech, or inappropriate behavior towards our tutors, staff, or other users.</li>
                            <li><strong>Misuse IP:</strong> Resell, redistribute, or use our proprietary content (questions, explanations) for commercial purposes outside of prepal.</li>
                        </ol>
                    </section>

                    <section id="disclaimers" className={styles.section} ref={el => sectionRefs.current['disclaimers'] = el}>
                        <h2 className={styles.sectionTitle}>8. Disclaimers and Liability</h2>
                        <h3 className={styles.subSectionTitle}>A. Disclaimers (&#8220;As Is&#8221;)</h3>
                        <p className={styles.text}>
                            We work hard to make prepal great, but we can't promise perfection. The Service is provided "as is" and "as available." We do not guarantee that your access will be uninterrupted, error-free, or that your scores will increase by a specific amount.
                        </p>
                        <h3 className={styles.subSectionTitle}>B. Limitation of Liability</h3>
                        <p className={styles.text}>
                            To the fullest extent permitted by Vietnamese law, the Company shall not be liable for any indirect, incidental, special, consequential, or punitive damages (including loss of profits, data, or goodwill) arising from your use or inability to use the Service.
                        </p>
                    </section>

                    <section id="governing-law" className={styles.section} ref={el => sectionRefs.current['governing-law'] = el}>
                        <h2 className={styles.sectionTitle}>9. Governing Law and Disputes</h2>
                        <h3 className={styles.subSectionTitle}>A. Governing Law</h3>
                        <p className={styles.text}>
                            These Terms of Service shall be governed by and construed in accordance with the laws of the Socialist Republic of Vietnam.
                        </p>
                        <h3 className={styles.subSectionTitle}>B. Dispute Resolution</h3>
                        <p className={styles.text}>
                            Any dispute arising out of or relating to these Terms shall first be attempted to be resolved through good-faith negotiation and mediation. If the dispute cannot be resolved within 30 days of initial notification, it shall be referred to the competent court in Ho Chi Minh City.
                        </p>
                    </section>

                    <section id="contact" className={styles.section} ref={el => sectionRefs.current['contact'] = el}>
                        <h2 className={styles.sectionTitle}>10. Contact Us</h2>
                        <p className={styles.text}>
                            If you have any questions, concerns, or need to report a problem, please reach out:
                        </p>
                        <div className={styles.contactCard}>
                            <div className={styles.contactRow}>
                                <span className={styles.contactLabel}>Company</span>
                                <span className={styles.contactValue}>prepal</span>
                            </div>
                            <div className={styles.contactRow}>
                                <span className={styles.contactLabel}>Email</span>
                                <a href={`mailto:${ContactConstant.INFO_EMAIL}`} className={styles.contactValue}>{ContactConstant.INFO_EMAIL}</a>
                            </div>
                            <div className={styles.contactRow}>
                                <span className={styles.contactLabel}>Website</span>
                                <a href="https://studyprepal.com" className={styles.contactValue}>https://studyprepal.com</a>
                            </div>
                        </div>
                        <p className={`${styles.text} ${styles.textItalic}`}>
                            Thank you for studying with prepal! We wish you the best on your test journey.
                        </p>
                    </section>

                    <div className={styles.bottomBar}>
                        <p>© {new Date().getFullYear()} Prepal Inc. All rights reserved.</p>
                        <div className={styles.bottomLinks}>
                            <Link to="/landing" className={styles.bottomLink}>Home</Link>
                            <Link to="/about-us" className={styles.bottomLink}>About Us</Link>
                            <Link to="/pricing" className={styles.bottomLink}>Pricing</Link>
                        </div>
                    </div>

                </main>
            </div>

            <PageFooter />
        </div>
    );
};

export default TermsOfServicePage;
